/**
 * DataViewToggle — segmented "As saved / Latest / Both / History" control for
 * saved plot comparison. Appears when reference quantities have changed since
 * the snapshot was taken. "History" mode appears when 3+ distinct S_ref values
 * exist, letting the user overlay multiple historical curves.
 */

import type { ComparisonViewMode } from "../../hooks/usePlotComparison";
import type { RefQuantityHistoryEntry } from "../../api/referenceQuantities";
import { ParameterHistoryStrip } from "./ParameterHistoryStrip";

interface Props {
  viewMode: ComparisonViewMode;
  onViewModeChange: (mode: ComparisonViewMode) => void;
  snapshotRefArea: number;
  liveRefArea: number;
  units: string | null;
  isLight: boolean;
  history?: RefQuantityHistoryEntry[];
  selectedHistoryValues?: Set<number>;
  onToggleHistoryValue?: (value: number) => void;
}

function buildModes(hasHistory: boolean): { value: ComparisonViewMode; label: string }[] {
  const modes: { value: ComparisonViewMode; label: string }[] = [
    { value: "old", label: "As saved" },
    { value: "new", label: "Latest" },
    { value: "both", label: "Both" },
  ];
  if (hasHistory) {
    modes.push({ value: "history", label: "History" });
  }
  return modes;
}

export function DataViewToggle({
  viewMode,
  onViewModeChange,
  snapshotRefArea,
  liveRefArea,
  units,
  isLight,
  history,
  selectedHistoryValues,
  onToggleHistoryValue,
}: Props) {
  const u = units ?? "";
  const hasHistory = (history?.length ?? 0) >= 3;
  const modes = buildModes(hasHistory);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        fontSize: 11,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            color: isLight ? "#9a6700" : "#e3b341",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          S_ref: {snapshotRefArea}{u} → {liveRefArea}{u}
        </span>

        <div
          style={{
            display: "inline-flex",
            borderRadius: 5,
            border: `1px solid ${isLight ? "#d0d5d2" : "#363d39"}`,
            overflow: "hidden",
          }}
        >
          {modes.map(({ value, label }, i) => {
            const active = value === viewMode;
            const isLast = i === modes.length - 1;
            return (
              <button
                key={value}
                onClick={() => onViewModeChange(value)}
                style={{
                  padding: "2px 10px",
                  fontSize: 11,
                  fontWeight: active ? 600 : 400,
                  fontFamily: "inherit",
                  border: "none",
                  cursor: "pointer",
                  background: active
                    ? (isLight ? "#00643C" : "#4ade80")
                    : "transparent",
                  color: active
                    ? (isLight ? "#fff" : "#0a0a0a")
                    : (isLight ? "#4A524D" : "#A3ADA7"),
                  borderRight: isLast
                    ? "none"
                    : `1px solid ${isLight ? "#d0d5d2" : "#363d39"}`,
                  transition: "background 0.15s, color 0.15s",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {viewMode === "history" && history && selectedHistoryValues && onToggleHistoryValue && (
        <ParameterHistoryStrip
          history={history}
          currentValue={liveRefArea}
          selectedValues={selectedHistoryValues}
          onToggleValue={onToggleHistoryValue}
          units={units}
          isLight={isLight}
        />
      )}
    </div>
  );
}
